/**
 * Derived metrics for the dashboard cards and alert strip.
 *
 * Inputs are the raw account documents from the server (one per row), each
 * carrying months[] / subTaxes[] cell strings serialized per spec § 5.
 * parseCell from cells.js is the only legal way to read those strings.
 */
import {
    parseCell,
    getCellStatus,
    getCellTotalPaid,
    isQuarterlyFilingMonth,
} from './cells';
import { TRACKERS } from '../config/trackers';

const REPORTABLE_STATUSES = ['paid', 'partial', 'pending', 'overdue', 'future', 'na'];

/** Aggregate counts per cell-status across one account's relevant slot array. */
function statusCountsForAccount(account) {
    const counts = REPORTABLE_STATUSES.reduce((acc, k) => {
        acc[k] = 0;
        return acc;
    }, {});
    const tracker = TRACKERS[account.trackerKey];
    if (!tracker) return counts;
    const isAnnual = tracker.frequency === 'annual';
    const cells = isAnnual ? account.subTaxes : account.months;
    if (!Array.isArray(cells)) return counts;
    cells.forEach((raw, i) => {
        // For monthly trackers tagged "quarterly" via filingFrequency,
        // off-quarter months render as 'na' visually but are stored as
        // 'na' or 'future' depending on backfill — count both as filler.
        const parsed = parseCell(raw);
        const status = getCellStatus(parsed);
        if (counts[status] !== undefined) counts[status] += 1;
        if (
            account.trackerKey === 'salesTax' &&
            account.fields?.filingFrequency === 'Quarterly' &&
            !isQuarterlyFilingMonth(i) &&
            status === 'future'
        ) {
            // shift the count: a future cell on a quarterly off-month is N/A.
            counts.future -= 1;
            counts.na += 1;
        }
    });
    return counts;
}

/** Sum of paid amounts across one account's slots — used in YTD displays. */
export function totalPaidForAccount(account) {
    const tracker = TRACKERS[account.trackerKey];
    if (!tracker) return 0;
    const isAnnual = tracker.frequency === 'annual';
    const cells = isAnnual ? account.subTaxes : account.months;
    if (!Array.isArray(cells)) return 0;
    return cells.reduce((sum, raw) => sum + getCellTotalPaid(parseCell(raw)), 0);
}

/** Tracker-card derivation. Returns shape used by Dashboard cards. */
export function deriveTrackerCard(trackerKey, accounts) {
    const tracker = TRACKERS[trackerKey];
    const rows = accounts.filter(
        (a) => a.trackerKey === trackerKey && a.status === 'active',
    );

    let overdue = 0;
    let pending = 0;
    let paid = 0;
    rows.forEach((row) => {
        const counts = statusCountsForAccount(row);
        overdue += counts.overdue;
        pending += counts.pending;
        paid += counts.paid + counts.partial;
    });

    let cardClass = 'ok';
    let badge = { text: 'On track', cls: 'badge-green' };
    let status = { text: paid > 0 ? `${paid} paid` : 'No activity', cls: 'green' };

    if (overdue > 0) {
        cardClass = 'alert';
        badge = { text: `${overdue} overdue`, cls: 'badge-red' };
        status = {
            text: `${overdue} overdue · ${pending} pending`,
            cls: 'red',
        };
    } else if (pending > 0) {
        cardClass = 'warn';
        badge = { text: `${pending} due`, cls: 'badge-amber' };
        status = { text: `${pending} payment${pending === 1 ? '' : 's'} pending`, cls: 'amber' };
    } else if (rows.length === 0) {
        cardClass = 'ok';
        badge = { text: 'Empty', cls: 'badge-neutral' };
        status = { text: 'No accounts yet', cls: 'neutral' };
    }

    const accountsLabel = `${rows.length} active account${rows.length === 1 ? '' : 's'}`;

    return {
        trackerKey,
        tracker,
        count: rows.length,
        cardClass,
        badge,
        status,
        accountsLabel,
    };
}

/** Belongs quick-view card derivation. */
export function deriveBelongsCard(belongsKey, accounts) {
    const rows = accounts.filter(
        (a) => a.belongs === belongsKey && a.status === 'active',
    );
    let overdue = 0;
    let pending = 0;
    let monthlyOut = 0;
    rows.forEach((row) => {
        const counts = statusCountsForAccount(row);
        overdue += counts.overdue;
        pending += counts.pending;
        const fieldAmt = pickMonthlyEstimate(row);
        if (!row.incoming) monthlyOut += fieldAmt;
    });
    return {
        count: rows.length,
        overdue,
        pending,
        monthlyOut,
    };
}

/** Best-effort estimate of a row's monthly cost from its declared fields. */
export function pickMonthlyEstimate(row) {
    const f = row.fields || {};
    const candidates = [
        f.monthlyPayment, f.avgMonthly, f.monthlyCost, f.monitoringCost,
        f.costPerService, f.premiumAmount, f.monthlyRent,
    ];
    for (const c of candidates) {
        const n = Number(String(c || '').replace(/[^\d.]/g, ''));
        if (n > 0) return n;
    }
    return 0;
}

/** Build the dashboard alert-strip summary line. */
export function buildAlertSummary(accounts) {
    const items = [];
    let totalIssues = 0;
    Object.keys(TRACKERS).forEach((key) => {
        const rows = accounts.filter(
            (a) => a.trackerKey === key && a.status === 'active',
        );
        let overdue = 0;
        let due = 0;
        rows.forEach((r) => {
            const c = statusCountsForAccount(r);
            overdue += c.overdue;
            due += c.pending;
        });
        if (overdue > 0) {
            items.push(`${overdue} ${TRACKERS[key].label.toLowerCase()} overdue`);
            totalIssues += overdue;
        } else if (due > 0) {
            items.push(`${due} ${TRACKERS[key].label.toLowerCase()} due`);
            totalIssues += due;
        }
    });
    return { items, totalIssues };
}
